// The rows behind a report tile — every order, invoice, PO, presentation,
// project or touchpoint the tile counts (Stephanie 2026-10-16: "i want to see
// the orders not just the numbers"). Search, sort by any column, a total at
// the bottom, and Download CSV for a spreadsheet. Each row opens its record.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, ArrowUpDown, Download, Search } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import type { DateRange } from "@/components/ReportShell";
import { cn } from "@/lib/utils";

import {
  LIST_COLUMNS,
  NOUN,
  cellText,
  filterAndSort,
  money,
  nextSort,
  type SortState,
  rowsToCsv,
  type ColKey,
  type ReportList,
  type ReportRow,
} from "@/lib/report-rows";

export type { ReportList, ReportRow };

interface RowsResult {
  rows: ReportRow[];
  total: string;
  unknownAmounts: number;
  truncated: boolean;
}

export function ReportRowsTable({
  list,
  range,
  repId,
  title,
  onlyStatus,
}: {
  list: ReportList;
  range: DateRange;
  repId?: string;
  title: string;
  /** Show only rows with this stored status (e.g. touchpoint type "call"). */
  onlyStatus?: string;
}) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<{ key: ColKey; dir: "asc" | "desc" } | null>(null);
  const cols = LIST_COLUMNS[list];
  const qs = new URLSearchParams({ list, from: range.from, to: range.to });
  if (repId) qs.set("rep_id", repId);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["reports", "rows", list, range.from, range.to, repId ?? ""],
    queryFn: () => api<RowsResult>(`/reports/rows?${qs.toString()}`),
  });
  const shown = useMemo(
    () => filterAndSort((data?.rows ?? []).filter((r) => !onlyStatus || r.status === onlyStatus), cols, search, sort),
    [data, cols, search, sort, onlyStatus],
  );
  const moneyCols = cols.filter((c) => c.money);
  const sumOf = (key: ColKey): string =>
    (shown.reduce((s, r) => s + (r[key] === null ? 0 : Math.round(Number(r[key]) * 100)), 0) / 100).toFixed(2);

  const download = () => {
    const blob = new Blob([rowsToCsv(shown, cols)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${range.from}-to-${range.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clickSort = (key: ColKey) => setSort((s) => nextSort(s, key));

  return (
    <section className="mt-6 rounded-lg border border-brown-100 bg-white" aria-label={title}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e7e7e4] px-5 py-3">
        <div>
          <h2 className="text-base font-semibold text-ink">{title}</h2>
          <p className="text-xs text-brown-300">
            {isLoading ? "Loading…" : `${shown.length.toLocaleString()} ${NOUN[list]}${search ? " match" : ""}`}
            {data?.truncated && " · showing the latest 500 — narrow the dates to see the rest"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-brown-300" />
            <input
              type="search"
              aria-label={`Search ${title}`}
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-56 rounded-md border border-rule bg-white pl-8 pr-2 text-sm text-ink"
            />
          </label>
          <Button type="button" variant="outline" size="sm" onClick={download} disabled={shown.length === 0}>
            <Download className="h-3.5 w-3.5" /> Download CSV
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto" role="region" aria-label={`${title} table`} tabIndex={0}>
        {isLoading ? (
          <div className="p-10 text-center text-sm text-brown-300">Loading…</div>
        ) : isError ? (
          <div className="p-10 text-center text-sm text-rust-600">Couldn't load these {NOUN[list]}.</div>
        ) : shown.length === 0 ? (
          <div className="p-10 text-center text-sm text-brown-300">
            {search ? `No ${NOUN[list]} match "${search}".` : `No ${NOUN[list]} in this range.`}
          </div>
        ) : (
          <table className="w-full min-w-[880px]">
            <thead className="bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
              <tr>
                {cols.map((c) => (
                  <SortHeader
                    key={c.key}
                    label={c.label}
                    sort={sort}
                    sortKey={c.key}
                    onSort={clickSort}
                    align={c.money ? "right" : "left"}
                    className="px-4 py-2.5"
                  />
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-brown-50 text-sm">
              {shown.map((r) => (
                <tr key={r.id} className="hover:bg-cream-50">
                  {cols.map((c, i) => {
                    const text = cellText(r, c);
                    return (
                      <td
                        key={c.key}
                        className={cn(
                          "px-4 py-2.5 align-top",
                          c.money ? "text-right font-semibold tabular-nums text-brown-500" : "text-brown-400",
                          i === 0 && "whitespace-nowrap",
                          (c.key === "date" || c.key === "inHandsDate") && "whitespace-nowrap",
                        )}
                      >
                        {i === 0 && r.href ? (
                          <Link to={r.href} className="font-semibold text-brown-500 hover:underline">
                            {text}
                          </Link>
                        ) : c.key === "projectName" && r.href && cols[0]?.key !== "number" ? (
                          <Link to={r.href} className="text-brown-500 hover:underline">{text}</Link>
                        ) : c.key === "clientName" && r.clientId ? (
                          <Link to={`/clients/${r.clientId}/overview`} className="hover:underline">{text}</Link>
                        ) : (
                          text
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            {moneyCols.length > 0 && (
              <tfoot className="border-t border-[#e7e7e4] bg-[#fafaf8] text-sm font-semibold text-ink">
                <tr>
                  {cols.map((c, i) => (
                    <td key={c.key} className={cn("px-4 py-2.5", c.money && "text-right tabular-nums")}>
                      {i === 0 ? "Total" : c.money ? money(sumOf(c.key)) : ""}
                    </td>
                  ))}
                </tr>
                {data && data.unknownAmounts > 0 && (
                  <tr>
                    <td colSpan={cols.length} className="px-4 pb-2.5 text-xs font-normal text-brown-300">
                      {data.unknownAmounts} {data.unknownAmounts === 1 ? "row has" : "rows have"} no amount yet ("—") and {data.unknownAmounts === 1 ? "isn't" : "aren't"} in the total.
                    </td>
                  </tr>
                )}
              </tfoot>
            )}
          </table>
        )}
      </div>
    </section>
  );
}

/** A report tile you can click to list its rows below. */
export function ReportTile({
  label,
  value,
  amount,
  amountLabel,
  sub,
  selected,
  onSelect,
}: {
  label: string;
  value: number;
  /** Dollars (fixed(2) string); omitted = the tile has no money. */
  amount?: string | null;
  amountLabel?: string;
  sub: string;
  selected?: boolean;
  onSelect?: () => void;
}) {
  const inner = (
    <>
      <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">{label}</div>
      <div className="mt-2 flex items-baseline justify-between gap-2">
        <span className="font-display text-3xl font-semibold text-brown-500">{value.toLocaleString()}</span>
        {amount !== undefined && (
          <span className="text-right text-sm font-semibold text-brown-400">
            {money(amount)}
            {amountLabel && <span className="block text-[10px] font-medium uppercase tracking-wide text-brown-300">{amountLabel}</span>}
          </span>
        )}
      </div>
      {sub && <p className="mt-2 text-xs text-brown-400">{sub}</p>}
      {onSelect && (
        <p className={cn("mt-3 text-xs font-semibold", selected ? "text-ink" : "text-brown-300")}>
          {selected ? "Showing below ↓" : "Show list"}
        </p>
      )}
    </>
  );
  const base = "flex flex-col rounded-lg border bg-white p-5 text-left";
  return onSelect ? (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(base, "transition-colors hover:border-brown-300", selected ? "border-ink ring-1 ring-ink" : "border-brown-100")}
    >
      {inner}
    </button>
  ) : (
    <div className={cn(base, "border-brown-100")}>{inner}</div>
  );
}

/** A column header you click to sort A→Z, and again for Z→A. */
export function SortHeader<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  align = "left",
  className,
}: {
  label: string;
  sortKey: K;
  sort: SortState<K> | null;
  onSort: (key: K) => void;
  align?: "left" | "right";
  className?: string;
}) {
  const on = sort?.key === sortKey;
  return (
    <th
      className={cn(className, align === "right" && "text-right")}
      aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        title={on && sort.dir === "asc" ? "Sort Z to A" : "Sort A to Z"}
        className={cn("group inline-flex items-center gap-1 whitespace-nowrap hover:text-ink", on && "text-ink", align === "right" && "flex-row-reverse")}
      >
        {label}
        {on ? (
          sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40 group-hover:opacity-100" />
        )}
      </button>
    </th>
  );
}
